import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { downloadBookingFormPdf } from '../../services/project';

const triggerPdfDownload = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const DownloadBookingTabPDF = ({ open, onClose, booking }) => {
    const [loadingData, setLoadingData] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) {
            setLoadingData(false);
            setGeneratingPdf(false);
            setError('');
            return;
        }

        setLoadingData(false);
        setError('');
    }, [open]);

    const generatePDF = async () => {
        if (!booking) return;

        const bookingId = booking?._original?._id || booking?._id || booking?.id;
        if (!bookingId) {
            setError('Booking ID not found.');
            return;
        }

        setGeneratingPdf(true);
        setError('');

        try {
            const blob = await downloadBookingFormPdf(bookingId);
            triggerPdfDownload(blob, `BookingForm_${booking?.bookingId || 'booking'}.pdf`);
            onClose?.();
        } catch (err) {
            console.error('Failed to download booking PDF', err);
            setError(err.message || 'Could not download booking form.');
        } finally {
            setGeneratingPdf(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <PictureAsPdfIcon color="error" /> Download Booking Form
            </DialogTitle>

            <DialogContent dividers>
                {loadingData && (
                    <Box display="flex" flexDirection="column" alignItems="center" py={3}>
                        <CircularProgress size={30} />
                        <Typography variant="caption" sx={{ mt: 1 }}>Fetching Details...</Typography>
                    </Box>
                )}

                {error && <Alert severity="error">{error}</Alert>}

                {!loadingData && !error && (
                    <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                        <Typography variant="body1" gutterBottom align="center">
                            Generate Booking Form for <strong>{booking?.bookingId}</strong>
                        </Typography>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={generatePDF}
                            disabled={generatingPdf || !booking}
                            startIcon={generatingPdf ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
                            sx={{ mt: 2, px: 4, borderRadius: 2 }}
                        >
                            {generatingPdf ? 'Generating...' : 'Download Form Now'}
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default DownloadBookingTabPDF;
